import React from "react"
import PropTypes from "prop-types"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import SelectComponent from "src/v2/components/select/select-component"
import { buildFinancialSeries } from "./company-financial-data"

const formatPeriod = (period) => {
    const [year, month] = String(period ?? "").split("-")

    return year && month ? `${month}/${year}` : "-"
}

const formatValue = (value, suffix = "") => {
    const number = Number(value)

    if (value === null || value === undefined || Number.isNaN(number)) return "-"

    return `${new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(number)}${suffix}`
}

const stickyCellSx = (theme) => ({
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
})

const FinancialTable = ({ title, periods, rows, unit, valueSuffix }) => (
    <Accordion disableGutters sx={{ backgroundColor: "background.paper", color: "common.white" }}>
        <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "icon.main" }} />}
            aria-label={title}
        >
            <Typography component="h3" variant="h6">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
            <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label={title} sx={{ minWidth: 560 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={stickyCellSx}>{unit}</TableCell>
                            {periods.map((period) => (
                                <TableCell key={period} align="right">{formatPeriod(period)}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell component="th" scope="row" sx={stickyCellSx}>{row.label}</TableCell>
                                {row.values.map((value, index) => (
                                    <TableCell key={`${row.id}-${periods[index]}`} align="right">
                                        {formatValue(value, valueSuffix)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </AccordionDetails>
    </Accordion>
)

FinancialTable.propTypes = {
    title: PropTypes.string.isRequired,
    periods: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        values: PropTypes.array.isRequired,
    })).isRequired,
    unit: PropTypes.string.isRequired,
    valueSuffix: PropTypes.string,
}

FinancialTable.defaultProps = {
    valueSuffix: "",
}

const CompanyFinancialSection = ({ financial }) => {
    const {
        status,
        error,
        availableGroups,
        selectedGroup,
        selectGroup,
        dataByGroup,
        reload,
    } = financial

    if (status === "idle") {
        return (
            <Typography color="common.white">
                Selecione uma empresa para consultar os indicadores financeiros.
            </Typography>
        )
    }

    if (status === "empty") {
        return (
            <Typography color="common.white">
                Não há indicadores financeiros e balanço patrimonial disponíveis para esta empresa.
            </Typography>
        )
    }

    if (status === "loading") {
        return (
            <Box aria-label="Carregando indicadores financeiros">
                <Skeleton variant="rectangular" height={120} />
            </Box>
        )
    }

    if (status === "error") {
        return (
            <Box>
                <Typography role="alert" color="common.white">
                    {error}
                </Typography>
                <Button variant="outlined" onClick={reload} sx={{ mt: 2, color: "common.white", borderColor: "common.white" }}>
                    TENTAR NOVAMENTE
                </Button>
            </Box>
        )
    }

    const selectedData = dataByGroup[selectedGroup]
    const { periods, balanceRows, indicatorRows } = buildFinancialSeries(selectedData?.records ?? [])

    return (
        <Box>
            {availableGroups.length > 1 && (
                <Box sx={{ maxWidth: 280 }}>
                    <SelectComponent
                        label="Grupo de balanço"
                        value={selectedGroup}
                        onChange={(event) => selectGroup(event.target.value)}
                        options={availableGroups.map((group) => ({ value: group, label: group }))}
                    />
                </Box>
            )}

            <Box sx={{ mt: 2, borderTop: 1, borderColor: "divider" }}>
                {balanceRows.length > 0 && (
                    <FinancialTable
                        title="Balanço Patrimonial"
                        periods={periods}
                        rows={balanceRows}
                        unit={selectedData.unit}
                    />
                )}
                {indicatorRows.length > 0 && (
                    <FinancialTable
                        title="Indicadores Financeiros"
                        periods={periods}
                        rows={indicatorRows}
                        unit="%"
                        valueSuffix="%"
                    />
                )}
            </Box>
        </Box>
    )
}

CompanyFinancialSection.propTypes = {
    financial: PropTypes.shape({
        status: PropTypes.string.isRequired,
        error: PropTypes.string,
        availableGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
        selectedGroup: PropTypes.string.isRequired,
        selectGroup: PropTypes.func.isRequired,
        dataByGroup: PropTypes.object.isRequired,
        reload: PropTypes.func.isRequired,
    }).isRequired,
}

export default CompanyFinancialSection
